import React, { useEffect, useRef, useState } from 'react';
import CustomTable from '../../share-components/CustomTable';
import { useNavigate } from 'react-router-dom';
import { PAGE_PURCHASE_LIST, ROUTE_PURCHASE_DETAILS, ROUTE_PURCHASE_EDIT, ROUTE_PURCHASE_LIST } from '../../constants/routeConstants';
import { Accordion, Button, Card } from 'react-bootstrap';
import { useAppContext } from '../../share-components/AppContext';
import {
    currentMonth, currentYear, customStorage, formatDateTime, getAvailableMonths, getChangingSelectColor, getPayloadData,
    handleFromMonthChange, handleFromYearChange, handleToMonthChange, handleToYearChange, isEqualNumber, isNullOrEmptyString, years
} from '../../services/UtilityService';
import { getActiveCategories, getPurchaseProducts, receivedPurchasedItem } from '../../services/ManagementService';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import AlertDialog from '../../share-components/AlertDialog';
import { DELI_TYPES, SELECT_ALL_DEFAULT, SERVER_ERROR_MESSAGE, TBL_DATA_SEPARATOR, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import useScrollRestoration from '../../share-components/UseScrollRestoration';
import SearchColumnSelect from '../../share-components/SearchColumnSelect';
import NoDataFound from '../../share-components/NoDataFound';
import FormTitle from '../../share-components/FormTitle';
import noImage from '../../assets/No_Image_Available.jpg';
import { getAllActiveUsers } from '../../services/CommonService';
import { getPrintInstocks, getPrintOrderInfo, getPrintPurchaseInfo } from '../../services/PrintPDFService';
import PrintPurchasedInfo from '../admin/PrintPurchasedInfo';
import PrintOrderedInfo from '../admin/PrintOrderedInfo';
import PrintInstockList from '../admin/PrintInstockList';

const PrintPDF = () => {

    useScrollRestoration(PAGE_PURCHASE_LIST);

    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();

    const [activeUsers, setActiveUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);

    const [purchasedById, setPurchasedById] = useState(currentUser?.userId || "");
    const [categoryId, setCategoryId] = useState("");
    const [purchaseFromYear, setPurchaseFromYear] = useState(currentYear);
    const [purchaseFromMonth, setPurchaseFromMonth] = useState(currentMonth);
    const [purchaseToYear, setPurchaseToYear] = useState(currentYear);
    const [purchaseToMonth, setPurchaseToMonth] = useState(currentMonth);
    const [printablePurchaseData, setPrintablePurchaseData] = useState([]);
    const [triggerPurchasePrintInfo, setTriggerPurchasePrintInfo] = useState(false);

    const [paymentReceivedById, setPaymentReceivedById] = useState("");
    const [deliveredById, setDeliveredById] = useState("");
    const [deliType, setDeliType] = useState("");
    const [orderedById, setOrderedById] = useState(currentUser?.userId || "");
    const [orderFromYear, setOrderFromYear] = useState(currentYear);
    const [orderFromMonth, setOrderFromMonth] = useState(currentMonth);
    const [orderToYear, setOrderToYear] = useState(currentYear);
    const [orderToMonth, setOrderToMonth] = useState(currentMonth);
    const [printableOrderData, setPrintableOrderData] = useState([]);
    const [triggerOrderPrintInfo, setTriggerOrderPrintInfo] = useState(false);

    const [printableInstocksData, setPrintableInstocksData] = useState([]);
    const [triggerInstocksPrint, setTriggerInstocksPrint] = useState(false);

    const printPurchaseRef = useRef();
    const printOrderRef = useRef();
    const printInstocksRef = useRef();

    useEffect(() => {
        setRouteNaviData("MANAGEMENT >> PRINT");
        fetchInitialLoad();
    }, []);

    const fetchInitialLoad = async () => {
        setLoading(true);
        try {
            await fetchActiveUsers();
            const categoryList = await getActiveCategories();
            let sortedCategories = (categoryList || [])?.sort((a, b) => (a.categoryName || "")?.localeCompare((b?.categoryName || "")));
            setCategories(sortedCategories);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const fetchActiveUsers = async () => {
        const response = await getAllActiveUsers();
        const sortedUsers = (response || [])?.sort((a, b) => (a.username || "")?.localeCompare((b?.username || "")));
        setActiveUsers(sortedUsers);
    }

    const printPurchaseInfo = async () => {
        setLoading(true);
        try {
            const searchDates = {
                fromYear: parseInt(purchaseFromYear),
                fromMonth: parseInt(purchaseFromMonth),
                toYear: parseInt(purchaseToYear),
                toMonth: parseInt(purchaseToMonth),
                purchasedById: purchasedById || null,
                categoryId: parseInt(categoryId) || null
            }
            const response = await getPrintPurchaseInfo(searchDates);
            const printData = {
                printHeader: "Purchase Histories",
                purchasedBy: activeUsers.find(user => user.userId === purchasedById)?.username || SELECT_ALL_DEFAULT,
                categoryName: categories.find(cat => isEqualNumber(cat.categoryId, categoryId))?.categoryName || SELECT_ALL_DEFAULT,
                targetYearMonth: {
                    fromYear: purchaseFromYear || 0,
                    fromMonth: purchaseFromMonth || 0,
                    toYear: purchaseToYear || 0,
                    toMonth: purchaseToMonth || 0
                },
                productListGroup: response?.result
            }
            setPrintablePurchaseData(printData);
            setTriggerPurchasePrintInfo(prev => !prev);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const printOrderInfo = async () => {
        setLoading(true);
        try {
            const searchDates = {
                fromYear: parseInt(orderFromYear),
                fromMonth: parseInt(orderFromMonth),
                toYear: parseInt(orderToYear),
                toMonth: parseInt(orderToMonth),
                orderedById: orderedById || null,
                paymentReceivedById: paymentReceivedById || null,
                deliveredById: deliveredById || null,
                deliType: deliType ? (parseInt(deliType, 10) || null) : null,
            }
            const response = await getPrintOrderInfo(searchDates);
            const printData = {
                printHeader: "Order Histories",
                orderedBy: activeUsers.find(user => user.userId === orderedById)?.username || SELECT_ALL_DEFAULT,
                paymentReceivedBy: activeUsers.find(user => user.userId === paymentReceivedById)?.username || SELECT_ALL_DEFAULT,
                deliveredBy: activeUsers.find(user => user.userId === deliveredById)?.username || SELECT_ALL_DEFAULT,
                deliveryType: DELI_TYPES.find(type => isEqualNumber(type.value, deliType))?.text || SELECT_ALL_DEFAULT,
                targetYearMonth: {
                    fromYear: orderFromYear || 0,
                    fromMonth: orderFromMonth || 0,
                    toYear: orderToYear || 0,
                    toMonth: orderToMonth || 0
                },
                productListGroup: response?.result
            }
            setPrintableOrderData(printData);
            setTriggerOrderPrintInfo(prev => !prev);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const printInstocks = async () => {
        setLoading(true);
        try {
            const response = await getPrintInstocks();
            const printData = {
                printHeader: "Instock List",
                productListGroup: response?.result
            }
            setPrintableInstocksData(printData);
            setTriggerInstocksPrint(prev => !prev);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleClickPrintPurchase();
    }, [triggerPurchasePrintInfo])
    const handleClickPrintPurchase = () => {
        if (printPurchaseRef.current) {
            printPurchaseRef.current.handlePrint();
        }
    };

    useEffect(() => {
        handleClickPrintOrder();
    }, [triggerOrderPrintInfo])
    const handleClickPrintOrder = () => {
        if (printOrderRef.current) {
            printOrderRef.current.handlePrint();
        }
    }; 

    useEffect(() => {
        handleClickPrintInstocks();
    }, [triggerInstocksPrint])
    const handleClickPrintInstocks = () => {
        if (printInstocksRef.current) {
            printInstocksRef.current.handlePrint();
        }
    };

    return (
        <div id='printPDFPage'>
            <FormTitle title={"Print PDF"} isCenter={true} />
            {isFirstLoaded && <>
                <div style={{ overflow: "auto", paddingBottom: "50px", }}>
                    <div style={{ minWidth: "max-content", maxWidth: "650px", margin: '0 auto' }}>
                        <Card className='mb-3'>
                            <Card.Header className='text-center'>
                                <strong>Print Purchase Histories</strong>
                            </Card.Header>
                            <Card.Body style={{ padding: '10px 5px 20px 5px' }}>
                                <div>
                                    <div className='mt-1 mb-1' style={{ display: 'flex', justifyContent: 'start', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                        {/* Purchased By */}
                                        <div className="d-inline">
                                            <label className="me-1 custom-label" htmlFor="purchasedBy" style={{ minWidth: "50px" }}>
                                                Purchased By
                                            </label>
                                            <div>
                                                <select
                                                    id="purchasedBy"
                                                    className={`customInputControl select-min-max-width`}
                                                    value={purchasedById}
                                                    onChange={(e) => setPurchasedById(e.target.value || '')}
                                                >
                                                    <option value="">--All--</option>
                                                    {activeUsers.map(activeUser => (
                                                        <option key={activeUser.userId} value={activeUser.userId}>{activeUser.username}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="d-inline">
                                            <label className="me-1 custom-label" htmlFor="category" style={{ minWidth: "50px" }}>
                                                Category
                                            </label>
                                            <div>
                                                <select
                                                    id="category"
                                                    className={`customInputControl select-min-max-width`}
                                                    value={categoryId}
                                                    onChange={(e) => setCategoryId(e.target.value || '')}
                                                >
                                                    <option value="">--All--</option>
                                                    {categories.map(category => (
                                                        <option key={category.categoryId} value={category.categoryId}>{category.categoryName}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-1 mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                        {/* From Year & Month */}
                                        <div className="d-inline">
                                            <label className="me-1 custom-label" style={{ minWidth: "50px" }}>
                                                From
                                            </label>
                                            <div>
                                                <select
                                                    id="purchaseFromYear"
                                                    className={`customInputControl`}
                                                    value={purchaseFromYear}
                                                    onChange={(e) => handleFromYearChange(e, purchaseFromMonth, purchaseToYear, purchaseToMonth, setPurchaseFromYear, setPurchaseFromMonth, setPurchaseToYear, setPurchaseToMonth)}
                                                >
                                                    {years.map((year) => (
                                                        <option key={year} value={year}>{year}</option>
                                                    ))}
                                                </select>
                                                <span className="separator">/</span>
                                                <select
                                                    id="purchaseFromMonth"
                                                    className={`customInputControl`}
                                                    value={purchaseFromMonth}
                                                    onChange={(e) => handleFromMonthChange(e, purchaseFromYear, purchaseToYear, purchaseToMonth, setPurchaseFromMonth, setPurchaseToMonth)}
                                                >
                                                    {getAvailableMonths(purchaseFromYear).map((month) => (
                                                        <option key={month} value={month}>
                                                            {month.toString().padStart(2, "0")}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* To Year & Month */}
                                        <div className="d-inline">
                                            <label className="me-1 custom-label" style={{ minWidth: "50px" }}>
                                                To
                                            </label>
                                            <div>
                                                <select
                                                    id="purchaseToYear"
                                                    className={`customInputControl`}
                                                    value={purchaseToYear}
                                                    onChange={(e) => handleToYearChange(e, purchaseFromYear, purchaseFromMonth, purchaseToMonth, setPurchaseFromYear, setPurchaseToYear, setPurchaseFromMonth)}
                                                >
                                                    {years.map((year) => (
                                                        <option key={year} value={year}>{year}</option>
                                                    ))}
                                                </select>
                                                <span className="separator">/</span>
                                                <select
                                                    id="purchaseToMonth"
                                                    className={`customInputControl`}
                                                    value={purchaseToMonth}
                                                    onChange={(e) => handleToMonthChange(e, purchaseFromYear, purchaseFromMonth, purchaseToYear, setPurchaseFromMonth, setPurchaseToMonth)}
                                                >
                                                    {getAvailableMonths(purchaseToYear).map((month) => (
                                                        <option key={month} value={month}>
                                                            {month.toString().padStart(2, "0")}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        {/* Hidden printable component */}
                                        <PrintPurchasedInfo ref={printPurchaseRef} printData={printablePurchaseData} />
                                    </div>
                                    <div className="mt-2">
                                        <button type='button' className="btn btn-success appBtn"
                                            onClick={printPurchaseInfo}
                                        >Print Purchase Histories (PDF) <i className='bi bi-download'></i></button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className='mb-3'>
                            <Card.Header className='text-center'>
                                <strong>Print Order Histories</strong>
                            </Card.Header>
                            <Card.Body style={{ padding: '10px 5px 20px 5px' }}>
                                <div className="mt-1 mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                    <div className="d-inline-block">
                                        <label className="custom-label" htmlFor="orderedBy" style={{ minWidth: "50px" }}>
                                            Ordered By
                                        </label>
                                        <div>
                                            <select
                                                id="orderedBy"
                                                className={`customInputControl select-min-max-width`}
                                                style={{
                                                    minWidth: '135px',
                                                    maxWidth: '150px'
                                                }}
                                                value={orderedById}
                                                onChange={(e) => setOrderedById(e.target.value)}
                                            >
                                                <option value={""}>{SELECT_ALL_DEFAULT}</option>
                                                {activeUsers.map(activeUser => (
                                                    <option key={activeUser.userId} value={activeUser.userId}>{activeUser.username}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="d-inline-block">
                                        <label className="custom-label" htmlFor="paymentReceivedBy" style={{ minWidth: "50px" }}>
                                            Pay Received By
                                        </label>
                                        <div>
                                            <select
                                                id="paymentReceivedBy"
                                                className={`customInputControl select-min-max-width`}
                                                style={{
                                                    minWidth: '135px',
                                                    maxWidth: '150px'
                                                }}
                                                value={paymentReceivedById}
                                                onChange={(e) => setPaymentReceivedById(e.target.value)}
                                            >
                                                <option value={""}>{SELECT_ALL_DEFAULT}</option>
                                                {activeUsers.map(activeUser => (
                                                    <option key={activeUser.userId} value={activeUser.userId}>{activeUser.username}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-1 mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                    <div className="d-inline-block">
                                        <label className="custom-label" htmlFor="deliveredBy" style={{ minWidth: "50px" }}>
                                            Delivered By
                                        </label>
                                        <div>
                                            <select
                                                id="deliveredBy"
                                                className={`customInputControl select-min-max-width`}
                                                style={{
                                                    minWidth: '135px',
                                                    maxWidth: '150px'
                                                }}
                                                value={deliveredById}
                                                onChange={(e) => setDeliveredById(e.target.value)}
                                            >
                                                <option value={""}>{SELECT_ALL_DEFAULT}</option>
                                                {activeUsers.map(activeUser => (
                                                    <option key={activeUser.userId} value={activeUser.userId}>{activeUser.username}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="d-inline-block">
                                        <label className="custom-label" htmlFor="deliType" style={{ minWidth: "50px" }}>
                                            Delivery Type
                                        </label>
                                        <div>
                                            <select
                                                id="deliType"
                                                className={`customInputControl select-min-max-width`}
                                                style={{
                                                    minWidth: '135px',
                                                    maxWidth: '150px'
                                                }}
                                                value={deliType}
                                                onChange={(e) => setDeliType(e.target.value)}
                                            >
                                                <option value={""}>{SELECT_ALL_DEFAULT}</option>
                                                {DELI_TYPES.map(type => (
                                                    <option key={type.value} value={type.value}>{type.text}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-1 mb-2" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                    {/* From Year & Month */}
                                    <div className="d-inline-block">
                                        <label className="me-1 custom-label" style={{ minWidth: "50px" }}>
                                            From
                                        </label>
                                        <div>
                                            <select
                                                id="orderFromYear"
                                                className={`customInputControl`}
                                                value={orderFromYear}
                                                onChange={(e) => handleFromYearChange(e, orderFromMonth, orderToYear, orderToMonth, setOrderFromYear, setOrderFromMonth, setOrderToYear, setOrderToMonth)}
                                            >
                                                {years.map((year) => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                            <span className="separator">/</span>
                                            <select
                                                id="orderFromMonth"
                                                className={`customInputControl`}
                                                value={orderFromMonth}
                                                onChange={(e) => handleFromMonthChange(e, orderFromYear, orderToYear, orderToMonth, setOrderFromMonth, setOrderToMonth)}
                                            >
                                                {getAvailableMonths(orderFromYear).map((month) => (
                                                    <option key={month} value={month}>
                                                        {month.toString().padStart(2, "0")}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* To Year & Month */}
                                    <div className="d-inline-block">
                                        <label className="me-1 custom-label" style={{ minWidth: "50px" }}>
                                            To
                                        </label>
                                        <div>
                                            <select
                                                id="orderToYear"
                                                className={`customInputControl`}
                                                value={orderToYear}
                                                onChange={(e) => handleToYearChange(e, orderFromYear, orderFromMonth, orderToMonth, setOrderFromYear, setOrderToYear, setOrderFromMonth)}
                                            >
                                                {years.map((year) => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                            <span className="separator">/</span>
                                            <select
                                                id="orderToMonth"
                                                className={`customInputControl`}
                                                value={orderToMonth}
                                                onChange={(e) => handleToMonthChange(e, orderFromYear, orderFromMonth, orderToYear, setOrderFromMonth, setOrderToMonth)}
                                            >
                                                {getAvailableMonths(orderToYear).map((month) => (
                                                    <option key={month} value={month}>
                                                        {month.toString().padStart(2, "0")}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                </div>

                                <div>
                                    {/* Hidden printable component */}
                                    <PrintOrderedInfo ref={printOrderRef} printData={printableOrderData} />
                                </div>
                                <div className="mt-2">
                                    <button type='button' className="btn btn-success appBtn"
                                        onClick={printOrderInfo}
                                    >Print Order Histories (PDF) <i className='bi bi-download'></i></button>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className='mb-3'>
                            <Card.Header className='text-center'>
                                <strong>Print Instock List</strong>
                            </Card.Header>
                            <Card.Body style={{ padding: '10px 5px 20px 5px' }}>
                                <div>
                                    <div>
                                        {/* Hidden printable component */}
                                        <PrintInstockList ref={printInstocksRef} printData={printableInstocksData} />
                                    </div>
                                    <div className="mt-2">
                                        <button type='button' className="btn btn-success appBtn"
                                            onClick={printInstocks}
                                        >Print Instock List (PDF) <i className='bi bi-download'></i></button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </>}
        </div>
    );
};

export default PrintPDF;